/**
 * The owner's activity feed for ONE property — pure derivation, no queries.
 *
 * Why derived rather than read from `audit_logs`: owners have no SELECT policy
 * on that table (and no INSERT either — see 20260816130100, where even a plain
 * admin needed a narrow policy added to read back rows they had just written).
 * A timeline built on it would be permanently empty in the owner portal.
 *
 * So every event here comes from a record the owner can already read, and each
 * one is a fact with a real timestamp on it: a document row's created_at, a
 * submission's reviewed_at, a booking's move_in_date. Nothing is inferred from
 * current state without a date to hang it on — an undated event has to be
 * placed somewhere in the order, and the only honest place is nowhere.
 *
 * The consequence, stated plainly because it will surprise someone: this feed
 * shows what HAPPENED to the property, not who clicked what. An owner editing
 * their own submission twice leaves one `submitted_at`, so it is one event. Per
 * -action history needs audit rows, which needs DB triggers (owner writes to
 * audit_logs are refused), and that is a bigger change than this file.
 *
 * Kept separate from the component so it is unit-testable, matching
 * src/lib/owner-activity.ts (the admin-side equivalent, keyed on a lessor).
 */
import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  FileText,
  LogIn,
  LogOut,
  Send,
  Trash2,
  Upload,
  Undo2,
} from "lucide-react";
import type { Tone } from "@/lib/owner-activity";
import type { ApartmentDocument } from "@/hooks/v2/useApartmentDocuments";
import type { OwnerDocument } from "@/hooks/owner/useOwnerPropertyDocuments";
import type {
  OwnerHistoryEntry,
  OwnerTenant,
} from "@/hooks/owner/useOwnerPropertyDetail";

/**
 * How this feed reaches the owner's language.
 *
 * The builder stays pure — it takes the same `(key, fallback)` translator the
 * components use rather than importing a context. Omitting it yields the English
 * fallbacks, which is what every unit test asserts and what a caller outside a
 * LanguageProvider gets.
 */
export type ActivityTranslator = (key: string, fallback: string) => string;

/** Substitutes {name}-style placeholders, the convention the translations
 *  table already uses ({count} in admin.lessor.linkedProperty). */
function fill(template: string, vars: Record<string, string>): string {
  return Object.entries(vars).reduce(
    (out, [name, value]) => out.split(`{${name}}`).join(value),
    template
  );
}

export type OwnerPropertyActivityEvent = {
  /** ISO timestamp — the sort key. */
  at: string;
  icon: typeof Upload;
  tone: Tone;
  title: string;
  /** The note, reason or filename behind the event. */
  detail?: string | null;
  /** Which room / apartment / document the event belongs to. */
  context?: string | null;
  /** Who did it, where the record names them. */
  actor?: string | null;
};

/** The submission lifecycle columns, as the owner portal can read them. */
export type ActivityPropertyRow = {
  submitted_at?: string | null;
  reviewed_at?: string | null;
  approval_status?: string | null;
  review_note?: string | null;
  withdrawn_at?: string | null;
  withdraw_reason?: string | null;
  created_at?: string | null;
};

export type BuildOwnerPropertyActivityInput = {
  property?: ActivityPropertyRow | null;
  /** Property/apartment/room/tenant documents, INCLUDING soft-deleted rows —
   *  a removal is an event, so the feed needs the rows the lists filter out. */
  documents?: ApartmentDocument[];
  /** Tenant-side documents and signed contracts (owner_property_documents). */
  tenantDocuments?: OwnerDocument[];
  tenants?: OwnerTenant[];
  history?: OwnerHistoryEntry[];
  /** apartment id → label, for naming a document's target. */
  apartmentLabels?: Map<number, string>;
  /** room id → title, same. */
  roomTitles?: Map<number, string>;
  /** Owner's language. Defaults to the English fallbacks. */
  t?: ActivityTranslator;
};

/** Drops anything undateable instead of sorting it to the epoch. */
function timeOf(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

function push(
  out: OwnerPropertyActivityEvent[],
  at: string | null | undefined,
  event: Omit<OwnerPropertyActivityEvent, "at">
) {
  if (timeOf(at) == null) return;
  out.push({ at: at as string, ...event });
}

/** "Apartment 2" / "Room 3" / "Whole property" — where a document is filed. */
function documentTarget(
  doc: ApartmentDocument,
  t: ActivityTranslator,
  apartmentLabels?: Map<number, string>,
  roomTitles?: Map<number, string>
): string {
  switch (doc.scope) {
    case "property":
      return t("owner.docs.scopeProperty", "Whole property");
    case "apartment":
      return (
        (doc.apartment_id != null
          ? apartmentLabels?.get(doc.apartment_id)
          : null) ?? t("owner.activity.anApartment", "An apartment")
      );
    case "room":
      return (
        (doc.room_id != null ? roomTitles?.get(doc.room_id) : null) ??
        t("owner.activity.aRoom", "A room")
      );
    case "tenant":
      return t("owner.activity.aTenancy", "A tenancy");
    default:
      return doc.scope;
  }
}

/** Who the document row says put it there, falling back to the side. */
function documentActor(doc: ApartmentDocument, t: ActivityTranslator): string {
  if (doc.uploader_name) return doc.uploader_name;
  return doc.source === "owner"
    ? t("owner.activity.anOwner", "An owner")
    : "coliviq";
}

/**
 * Every dated event on one property, newest first.
 *
 * Ties are broken by title so the order is stable across renders — two
 * documents uploaded in the same second must not swap places on every
 * refetch, which is what an unstable comparator does to a keyed list.
 */
export function buildOwnerPropertyActivity(
  input: BuildOwnerPropertyActivityInput
): OwnerPropertyActivityEvent[] {
  const out: OwnerPropertyActivityEvent[] = [];
  const {
    property,
    documents = [],
    tenantDocuments = [],
    tenants = [],
    history = [],
    apartmentLabels,
    roomTitles,
  } = input;
  const t: ActivityTranslator = input.t ?? ((_key, fallback) => fallback);

  // ── The submission lifecycle
  if (property) {
    push(out, property.submitted_at, {
      icon: Send,
      tone: "sky",
      title: t("owner.activity.submitted", "Submitted for approval"),
      detail: t(
        "owner.activity.submittedDetail",
        "Sent to coliviq for review."
      ),
    });

    /* The decision is dated by reviewed_at and named by approval_status. A
       reviewed_at with a status of 'pending' is a resubmission that cleared the
       old decision (owner_resubmit_property resets both), so it has no decision
       to show — the submitted_at above already covers it. */
    if (property.reviewed_at) {
      if (property.approval_status === "approved") {
        push(out, property.reviewed_at, {
          icon: CheckCircle2,
          tone: "emerald",
          title: t("owner.activity.approved", "Approved by coliviq"),
          detail:
            property.review_note ??
            t("owner.activity.approvedDetail", "The property is live."),
        });
      } else if (property.approval_status === "rejected") {
        push(out, property.reviewed_at, {
          icon: AlertTriangle,
          tone: "amber",
          title: t(
            "owner.activity.changesRequested",
            "Changes requested by coliviq"
          ),
          detail: property.review_note ?? null,
        });
      }
    }

    push(out, property.withdrawn_at, {
      icon: Undo2,
      tone: "slate",
      title: t("owner.activity.withdrawn", "Submission withdrawn"),
      detail: property.withdraw_reason ?? null,
    });
  }

  // ── Documents filed at any level, and the ones taken back down
  for (const doc of documents) {
    const target = documentTarget(doc, t, apartmentLabels, roomTitles);
    push(out, doc.created_at, {
      icon: Upload,
      tone: doc.source === "owner" ? "teal" : "slate",
      title: fill(
        t("owner.activity.docUploaded", "Document uploaded: {title}"),
        { title: doc.title }
      ),
      context: target,
      actor: documentActor(doc, t),
      detail: doc.message ?? null,
    });
    /* A soft delete keeps the row, so the upload above still renders — the
       feed shows a file arriving and later being withdrawn, which is the
       history. `deleted_at` has no actor column: only the uploader (or staff)
       can delete, per RLS, so naming the uploader here would be a guess. */
    push(out, doc.deleted_at, {
      icon: Trash2,
      tone: "rose",
      title: fill(t("owner.activity.docRemoved", "Document removed: {title}"), {
        title: doc.title,
      }),
      context: target,
      detail: doc.delete_reason ?? null,
    });
  }

  // ── Tenant-side documents and signed contracts
  for (const doc of tenantDocuments) {
    // A row on the tenant's table is not necessarily the tenant's doing: staff
    // file documents on a tenancy from the admin area, and we generate some
    // ourselves. `source` is the only field that can tell them apart — an admin
    // who is also a tenant of the unit records the same uploader either way.
    const byColiviq = doc.source === "admin" || doc.source === "system";
    push(out, doc.created_at, {
      icon: FileText,
      tone: "sky",
      title: fill(
        t("owner.activity.tenantDocAdded", "Tenant document added: {title}"),
        {
          title:
            doc.title ??
            doc.file_name ??
            t("owner.activity.untitled", "Untitled"),
        }
      ),
      actor: byColiviq
        ? "coliviq"
        : (doc.tenant_name ?? t("owner.activity.aTenant", "A tenant")),
      context:
        [doc.room_title, doc.tenant_name].filter(Boolean).join(" · ") || null,
      detail: doc.status
        ? fill(t("owner.activity.docStatus", "Status: {status}"), {
            status: doc.status.replace(/_/g, " "),
          })
        : null,
    });
  }

  // ── Tenancies: the milestones that carry a date
  for (const tenant of tenants) {
    const who = tenant.name ?? t("owner.activity.aTenant", "A tenant");
    push(out, tenant.move_in_date, {
      icon: LogIn,
      tone: "emerald",
      title: fill(t("owner.activity.movedIn", "{name} moved in"), {
        name: who,
      }),
      context: tenant.room_title,
    });
    /* A move-out date on a CURRENT tenant is notice given, not a departure —
       the tenancy is still live (that is what puts it in `tenants` rather than
       `history`), so it reads as the future event it is. */
    push(out, tenant.move_out_date, {
      icon: LogOut,
      tone: "amber",
      title: fill(t("owner.activity.movingOut", "{name} is moving out"), {
        name: who,
      }),
      context: tenant.room_title,
    });
    push(out, tenant.last_payment_received_at, {
      icon: Banknote,
      tone: "emerald",
      title: fill(
        t("owner.activity.rentReceived", "Rent payment received from {name}"),
        { name: who }
      ),
      context: tenant.room_title,
    });
  }

  for (const past of history) {
    push(out, past.move_out_date, {
      icon: LogOut,
      tone: "slate",
      title: fill(t("owner.activity.movedOut", "{name} moved out"), {
        name: past.name ?? t("owner.activity.aTenant", "A tenant"),
      }),
      context: past.room_title,
    });
  }

  return out.sort((a, b) => {
    const diff = (timeOf(b.at) ?? 0) - (timeOf(a.at) ?? 0);
    return diff !== 0 ? diff : a.title.localeCompare(b.title);
  });
}
